import { FormEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { requestEmailOtp, verifyEmailOtp } from '../services/authService';

type LocationState = {
  email?: string;
  rememberMe?: boolean;
};

export const VerifyOtpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { email, rememberMe = false } = (location.state as LocationState) ?? {};
  const [otp, setOtp] = useState('');
  const [deviceName, setDeviceName] = useState('web-client');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email) {
      navigate('/login');
    }
  }, [email, navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email) return;

    setError(null);
    setLoading(true);

    try {
      const response = await verifyEmailOtp(email, otp.trim(), deviceName.trim() || 'web-client', rememberMe);
      login(response.token, response.user);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) return;

    setError(null);
    setMessage(null);
    setLoading(true);

    try {
      await requestEmailOtp(email);
      setMessage('A new code has been sent to your inbox.');
      setOtp(''); // Clear OTP input
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Could not resend code. Please try again soon.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <main className="login-content" style={{ alignItems: 'center' }}>
        <div className="login-card">
          <h2>Verify OTP</h2>
          <p>
            A six-digit code was {email ? `sent to ${email}.` : 'issued for your account.'} Enter it below to finish
            signing in.
          </p>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="otp">One-time passcode</label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                placeholder="123456"
                value={otp}
                onChange={(event) => setOtp(event.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="deviceName">Device name</label>
              <input
                id="deviceName"
                type="text"
                value={deviceName}
                onChange={(event) => setDeviceName(event.target.value)}
                placeholder="web-client"
              />
            </div>

            {error && <div className="error">{error}</div>}
            {message && (
              <div style={{ color: '#0b4d99', fontSize: '0.85rem', marginBottom: 12, fontWeight: 500 }}>{message}</div>
            )}

            <button className="btn" type="submit" disabled={loading}>
              {loading ? 'Verifying…' : 'Verify & Sign in'}
            </button>
          </form>

          <div style={{ marginTop: 12, textAlign: 'center' }}>
            <button
              type="button"
              onClick={handleResend}
              disabled={loading}
              className="btn btn-secondary"
              style={{ width: '100%' }}
            >
              Resend OTP
            </button>
            <button className="btn btn-secondary" type="button" onClick={() => navigate('/login')} disabled={loading}>
              Back to login
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

